import React, { useCallback, useMemo, useState } from "react";
import { FormInput } from "shared/components/Inputs/FormInput";
import { Button, IconButton, Skeleton, TextField, Typography } from "@mui/material";
import { XMarkIcon } from "shared/icons/XMarkIcon";
import { Wysiwyg } from "shared/components/Inputs/Wysiwyg";
import { LoadingButton } from "@mui/lab";
import { SurveyBlocks } from "modules/MainSurveyEditor/surveyBlocks";
import { ConnectToEntitiesInput } from "./ConnectToEntitiesInput";

export enum AddEditFormFieldsType {
  Text = "text",
  Wysiwyg = "wysiwyg",
  Connect = "connect",
}

export const AddEditEntityForm = (props: AddEditEntityFormProps) => {
  const {
    isNew,
    entityId,
    newTitle,
    onClose,
    onSave,
    onDelete,
    isLoading,
    fields,
    entityTitle,
    isJustDeleteWhenEditing,
  } = props;
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const formInputs = useMemo(() => {
    return fields.map((field, index) => {
      switch (field.inputType) {
        case AddEditFormFieldsType.Text:
          return (
            <FormInput
              title={field.title}
              key={index}
              fieldInput={
                <TextField
                  fullWidth
                  inputProps={{ maxLength: SurveyBlocks.BlockContentCharLimit.title }}
                  onChange={(e) => field.onChange(e.target.value)}
                  value={field.value}
                  variant="outlined"
                  disabled={isJustDeleteWhenEditing && !isNew}
                />
              }
            />
          );
        case AddEditFormFieldsType.Wysiwyg:
          return (
            <FormInput
              key={index}
              title={field.title}
              fieldInput={
                <Wysiwyg
                  savedData={field.value}
                  onChange={field.onChange}
                  disabled={isJustDeleteWhenEditing && !isNew}
                />
              }
            />
          );
        case AddEditFormFieldsType.Connect:
          return (
            <FormInput
              title={field.title}
              key={index}
              fieldInput={
                <ConnectToEntitiesInput
                  onChange={field.onChange}
                  value={field.value}
                  options={field.options ?? []}
                  selectLabel={field.selectLabel}
                />
              }
            />
          );

        default:
          return null;
      }
    });
  }, [fields, isNew, isJustDeleteWhenEditing]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    await onSave();
    setIsSaving(false);
    onClose();
  }, [onSave, onClose]);

  const handleDelete = useCallback(() => {
    onDelete();
    onClose();
  }, [onDelete, onClose]);

  const isDisabled = fields.some((field) => field.isRequired && !field.value);

  return (
    <div className="layout-entity-form">
      <div className="_d-flex-ali-center-jc-sb-sm-gap form-header">
        <Typography variant="h3">{isNew ? newTitle ?? "New" : entityTitle}</Typography>
        <IconButton onClick={onClose}>
          <XMarkIcon />
        </IconButton>
      </div>

      {isLoading ? (
        <div className="_d-grid-md-gap-alic-start">
          <Skeleton />
          <Skeleton width="80%" />
          <Skeleton width="90%" />
        </div>
      ) : (
        <div className="form-body">{formInputs}</div>
      )}

      <div className="form-actions">
        {entityId && (
          <Button variant="text" color="error" className="delete-btn" onClick={handleDelete}>
            Delete
          </Button>
        )}

        <div className="_d-flex-ali-center-md-gap">
          <Button color="secondary" onClick={onClose}>
            Cancel
          </Button>
          {(isNew || !isJustDeleteWhenEditing) && (
            <LoadingButton variant="contained" onClick={handleSave} loading={isSaving} disabled={isDisabled}>
              {isNew ? "Create" : "Save"}
            </LoadingButton>
          )}
        </div>
      </div>
    </div>
  );
};

interface AddEditEntityFormProps {
  isNew: boolean;
  entityId: number | string | null;
  isLoading: boolean;
  fields: AddEditFormFields[];
  onClose: () => void;
  onSave: () => void;
  onDelete: () => void;
  entityTitle?: string;
  newTitle?: string;
  isJustDeleteWhenEditing?: boolean;
}

export interface AddEditFormFields {
  title: string;
  inputType: AddEditFormFieldsType;
  value: string;
  onChange: (value: string, isRemove?: boolean) => void;
  isRequired?: boolean;
  options?: { label: string; value: string }[];
  selectLabel?: string;
}
